import { useEffect } from "react";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "@/lib/firebase";
import { useSetFcmToken } from "@/hooks/useNotifications";
import { playNotificationSound } from "@/lib/notificationSound";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export const NotificationInitializer = () => {
  const { mutate: setFcmToken } = useSetFcmToken();
  const queryClient = useQueryClient();

  useEffect(() => {
    const setupNotifications = async () => {
      if (!messaging) return;

      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          // Manually register service worker to avoid registration timeout errors
          const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
            scope: "/firebase-cloud-messaging-push-scope",
          });

          const token = await getToken(messaging, {
            serviceWorkerRegistration: registration,
          });

          if (token) {
            setFcmToken({ device_token: token });
          }
        }
      } catch (error) {
        console.error("Error setting up notifications:", error);
      }
    };

    setupNotifications();

    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === "PLAY_NOTIFICATION_SOUND") {
        playNotificationSound();
      }
    };
    navigator.serviceWorker?.addEventListener("message", onSwMessage);

    // Foreground message handler
    if (messaging) {
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log("Foreground message received:", payload);
        playNotificationSound();
        toast.info(payload.notification?.title || "New Notification", {
          description: payload.notification?.body,
        });
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      });
      return () => {
        unsubscribe();
        navigator.serviceWorker?.removeEventListener("message", onSwMessage);
      };
    }

    return () => {
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
    };
  }, [setFcmToken, queryClient]);

  return null;
};
